import { ENV } from '@environment';
import { profile, setAccessToken, axiosConfig, setCountryCode } from 'api';
import { AxiosResponse } from 'axios';
import { Dispatch } from 'redux';
import { MyThunkResult } from 'store';

import { resetStore } from 'action-creators/generic';
import {
  loginError,
  refreshTokenSuccessful,
  refreshTokenError,
  userLogout,
  userLogoutSuccessful,
  userLogoutError,
  accountRequest,
  accountSuccessful,
  accountFailure,
  userAccountData,
} from 'action-creators/user';
import { changeLanguage } from 'i18n';
import { AdminUser } from 'models/adminUser';
import { Response } from 'models/response';
import { RefreshTokenResponse, AccountResponse } from 'models/user';
import { amplitudelogEvent } from 'services/amplitudeService';
import { OAuthRequest, postExchange } from 'services/oauthService';
import { amplitudeEvents } from 'utils/amplitudeEvents';

import { RootState } from './../store';
import { setCountry } from './i18n';

interface UserAccountResponse {
  data: AdminUser;
}

export const rappiLogin = (token: string) => async (dispatch: Dispatch): Promise<AccountResponse> =>
  new Promise(async (resolve, reject) => {
    setAccessToken(token);

    try {
      const response = await dispatch<any>(getAccount());

      setCountryCode(response.countryId);

      resolve(response);
    } catch (e) {
      dispatch(loginError(e));
      reject(e);
    }
  });

export const rappiFromOAuthLogin = (code: string, auth: boolean) => async (dispatch: Dispatch): Promise<any> =>
  new Promise(async (resolve, reject) => {
    try {
      const request: OAuthRequest = {
        code: code,
        client_id: ENV.RAPPI_OAUTH.LOGIN_CLIENTID,
        redirect_uri: ENV.RAPPI_OAUTH.REDIRECT_URL_FROM_LOGIN,
        grant_type: 'authorization_code',
      };

      const responseToken = await postExchange(request);

      if (auth) {
        setAccessToken(responseToken.access_token);

        const response = await dispatch<any>(getAccount());

        setCountryCode(response.countryId);

        resolve(response);
      }

      resolve(responseToken);
    } catch (e) {
      dispatch(loginError(e));
      reject(e);
    }
  });

export const getRefreshToken = (refreshToken: string) => async (dispatch: Dispatch) =>
  new Promise(async (resolve, reject) => {
    try {
      const {
        data: { data },
      } = await profile.get<Response<RefreshTokenResponse>>(`Users/refresh-token?Refresh${refreshToken}`, axiosConfig);
      dispatch(refreshTokenSuccessful(data));
      setAccessToken(data.accessToken);
      resolve(data);
    } catch (error) {
      dispatch(refreshTokenError(error));
      dispatch<any>(onUserLogout());
      reject(error);
    }
  });

export const onUserLogout = () => async (dispatch: Dispatch) =>
  new Promise<void>(async (resolve, reject) => {
    localStorage.clear();
    sessionStorage.clear();
    dispatch(userLogout());
    try {
      dispatch(resetStore());
      dispatch(userLogoutSuccessful());
      resolve();
    } catch (error) {
      dispatch(userLogoutError(error));
      reject(error);
    }
  });

export const updateAccount = (dispatch: Dispatch, data: AccountResponse): void => {
  const langs = ['es-CO', 'es-AR', 'es-MX', 'pt-BR', 'en-US'];
  changeLanguage(data.language || langs.find((lang) => lang.endsWith(data.countryId)) || 'es-CO');
  dispatch(accountSuccessful(data));
  dispatch(
    setCountry({
      id: data.countryId,
      name: data.countryName,
      culture: data.countryCulture,
      currency: data.countryCurrency,
      documentType: data.countryDocumentType,
    }) as any,
  );
};

export const getAccount = (branchCompanyId?: string) => async (
  dispatch: Dispatch,
  getState: () => RootState,
): Promise<AccountResponse> =>
  new Promise(async (resolve, reject) => {
    dispatch(accountRequest());
    const axiosConfig = { data: {} };

    const loginByBranchCompany = branchCompanyId ? `/${branchCompanyId}` : '';
    try {
      const {
        data: { data },
      } = await profile.get<Response<AccountResponse>>(`Users/GetMyAccount${loginByBranchCompany}`, axiosConfig);

      if (data.sessionToken === '' || data.sessionToken === undefined || data.sessionToken === null) {
        throw 'sessionToken is empty';
      }

      updateAccount(dispatch, data);
      resolve(data);
    } catch (error) {
      const { accountResult } = getState().user;
      await amplitudelogEvent(amplitudeEvents.TRAVEL_SEAMLESS_ERROR, { ERROR_MESSAGE: error }, accountResult);
      dispatch(accountFailure(error));
      reject(error);
    }
  });

export const fetchUserAccount = (): MyThunkResult<Promise<void>> => (dispatch) =>
  profile.get<UserAccountResponse>('/users', axiosConfig).then((response) => {
    const { data } = response.data;
    dispatch(userAccountData(data));
  });

export const confirmEmail = async (token: string) =>
  profile.put<void, AxiosResponse<Response<AccountResponse>>>(`/Users/EmailConfirmation/${token}`);

export const deleteUser = async (userId: string): Promise<AxiosResponse<Response<any>>> =>
  await profile.put(`Users/UnlinkRappiUserV2/${userId}`).catch((error) => {
    throw error;
  });
